import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import SideDrawer from '../common/SideDrawer';
import Button from '../common/Button';
import ConfirmationDialog from '../common/ConfirmationDialog';
import apiService from '../../services/ApiService';
import { apiRoutes } from '../../shared/apiRoutes';
import { ApiTenant } from '../../shared/interfaces';
import { useAuthorization } from '../../auth/useAuthorization';
import { Role } from '../../auth/roles';
import { clearAllNotifications } from '../../utils/notificationApi';

interface TenantOption {
  id: number;
  label: string;
}

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

/** Value for `<input type="datetime-local" />` in local time. */
function toDatetimeLocalValue(d: Date): string {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}T${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

const CreateNotification: React.FC = () => {
  const navigate = useNavigate();
  const { hasRole, isAuthenticated } = useAuthorization();
  const isSystemAdmin = hasRole(Role.ADMIN);
  const [drawerOpen, setDrawerOpen] = useState(true);
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [allTenants, setAllTenants] = useState(true);
  const [tenantOptions, setTenantOptions] = useState<TenantOption[]>([]);
  const [selectedTenantIds, setSelectedTenantIds] = useState<number[]>([]);
  const [loadingTenants, setLoadingTenants] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [tenantSearch, setTenantSearch] = useState('');
  const [displayFrom, setDisplayFrom] = useState(() => toDatetimeLocalValue(new Date()));
  const [displayTo, setDisplayTo] = useState(() =>
    toDatetimeLocalValue(new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)),
  );

  const filteredTenantOptions = useMemo(() => {
    const q = tenantSearch.trim().toLowerCase();
    if (!q) return tenantOptions;
    return tenantOptions.filter(
      (t) =>
        t.label.toLowerCase().includes(q) || String(t.id).includes(q),
    );
  }, [tenantOptions, tenantSearch]);

  useEffect(() => {
    if (isAuthenticated === false) {
      navigate('/login', { replace: true });
      return;
    }
    if (isAuthenticated && !isSystemAdmin) {
      window.dispatchEvent(
        new CustomEvent('show-toast', {
          detail: {
            message: 'Only administrators can manage notifications.',
            type: 'error',
          },
        }),
      );
      navigate('/game/oneday#home', { replace: true });
    }
  }, [isAuthenticated, isSystemAdmin, navigate]);

  useEffect(() => {
    if (!isAuthenticated || !isSystemAdmin) return;

    const loadTenants = async () => {
      setLoadingTenants(true);
      try {
        const response = await apiService.get<ApiTenant[] | { data?: ApiTenant[] }>(apiRoutes.tenants.list);
        let raw: ApiTenant[] = [];
        if (Array.isArray(response)) {
          raw = response;
        } else if (response && typeof response === 'object' && Array.isArray((response as any).data)) {
          raw = (response as any).data;
        }
        const opts: TenantOption[] = raw.map((t) => {
          const id = t.id ?? (t as any).tenantId ?? (t as any).tenant_id ?? 0;
          const name =
            t.tenantName || (t as any).tenant_name || (t as any).name || `Tenant ${id}`;
          return { id, label: name };
        });
        setTenantOptions(opts.filter((o) => o.id > 0));
      } catch (e) {
        console.error('Failed to load tenants', e);
        window.dispatchEvent(
          new CustomEvent('show-toast', {
            detail: { message: 'Failed to load tenants', type: 'error' },
          }),
        );
      } finally {
        setLoadingTenants(false);
      }
    };

    loadTenants();
  }, [isAuthenticated, isSystemAdmin]);

  const clearTargetTenantId = useMemo(() => {
    if (!isSystemAdmin) return undefined;
    if (allTenants) return undefined;
    if (selectedTenantIds.length === 1) return selectedTenantIds[0];
    return undefined;
  }, [isSystemAdmin, allTenants, selectedTenantIds]);

  const clearConfirmMessage = useMemo(() => {
    const scope =
      clearTargetTenantId != null
        ? ` for tenant #${clearTargetTenantId}`
        : isSystemAdmin
          ? ' for all tenants'
          : ' for your tenant';
    return `This permanently removes notifications${scope} and cannot be undone.`;
  }, [clearTargetTenantId, isSystemAdmin]);

  const handleClearAll = async () => {
    setClearing(true);
    try {
      const result = await clearAllNotifications(clearTargetTenantId);
      setShowClearConfirm(false);
      window.dispatchEvent(
        new CustomEvent('show-toast', {
          detail: {
            message:
              result.message ||
              `Cleared ${result.deletedCount ?? 0} notification(s).`,
            type: 'success',
          },
        }),
      );
      window.dispatchEvent(new CustomEvent('notifications-cleared'));
    } catch (err: unknown) {
      const msg =
        (err as { message?: string; error?: string })?.message ??
        (err as { error?: string })?.error ??
        'Failed to clear notifications';
      window.dispatchEvent(
        new CustomEvent('show-toast', {
          detail: { message: String(msg), type: 'error' },
        }),
      );
    } finally {
      setClearing(false);
    }
  };

  const handleClose = () => {
    setDrawerOpen(false);
    navigate('/home');
  };

  const toggleTenant = (id: number) => {
    setSelectedTenantIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const titleText = title.trim();
    const text = message.trim();
    if (!titleText) {
      window.dispatchEvent(
        new CustomEvent('show-toast', {
          detail: { message: 'Please enter a title.', type: 'error' },
        }),
      );
      return;
    }
    if (!text) {
      window.dispatchEvent(
        new CustomEvent('show-toast', {
          detail: { message: 'Please enter a message.', type: 'error' },
        }),
      );
      return;
    }
    if (!allTenants && selectedTenantIds.length === 0) {
      window.dispatchEvent(
        new CustomEvent('show-toast', {
          detail: {
            message: 'Select at least one tenant, or choose All tenants.',
            type: 'error',
          },
        }),
      );
      return;
    }

    if (!displayFrom.trim() || !displayTo.trim()) {
      window.dispatchEvent(
        new CustomEvent('show-toast', {
          detail: {
            message: 'Please set display period (from and to date & time).',
            type: 'error',
          },
        }),
      );
      return;
    }

    const fromDate = new Date(displayFrom);
    const toDate = new Date(displayTo);
    if (Number.isNaN(fromDate.getTime()) || Number.isNaN(toDate.getTime())) {
      window.dispatchEvent(
        new CustomEvent('show-toast', {
          detail: { message: 'Invalid display period dates.', type: 'error' },
        }),
      );
      return;
    }
    if (toDate.getTime() < fromDate.getTime()) {
      window.dispatchEvent(
        new CustomEvent('show-toast', {
          detail: { message: 'Display end must be on or after display start.', type: 'error' },
        }),
      );
      return;
    }

    setSubmitting(true);
    try {
      const body: Record<string, unknown> = {
        title: titleText,
        message: text,
        allTenants,
        displayFrom: fromDate.toISOString(),
        displayTo: toDate.toISOString(),
      };
      if (!allTenants) {
        body.tenantIds = selectedTenantIds;
      }

      await apiService.post(apiRoutes.notifications.create, body);
      window.dispatchEvent(
        new CustomEvent('show-toast', {
          detail: { message: 'Notification saved.', type: 'success' },
        }),
      );
      handleClose();
    } catch (err: any) {
      const msg =
        err?.message ||
        err?.error ||
        (typeof err === 'string' ? err : 'Failed to save notification');
      window.dispatchEvent(
        new CustomEvent('show-toast', {
          detail: { message: String(msg), type: 'error' },
        }),
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (!isAuthenticated || !isSystemAdmin) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center text-slate-600 text-sm">
        Checking access…
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100">
      <div className="p-4 max-w-2xl">
        <h1 className="text-lg font-semibold text-slate-800">
          {isSystemAdmin ? 'Create notification' : 'Notifications'}
        </h1>
        <p className="text-sm text-slate-600 mt-1">
          {isSystemAdmin
            ? 'Broadcast a message from the master database. Set when it should appear, then choose tenants.'
            : 'Manage notifications for your tenant.'}
        </p>
      </div>

      <SideDrawer
        isOpen={drawerOpen}
        onClose={handleClose}
        title={isSystemAdmin ? 'Create notification' : 'Notifications'}
        width="w-[min(100vw-1rem,28rem)]"
      >
        {isSystemAdmin ? (
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div>
            <label htmlFor="notification-title" className="block text-sm font-medium text-gray-700 mb-1">
              Title
            </label>
            <input
              id="notification-title"
              name="title"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              placeholder="Enter notification title…"
              disabled={submitting}
            />
          </div>

          <div>
            <label htmlFor="notification-message" className="block text-sm font-medium text-gray-700 mb-1">
              Message
            </label>
            <textarea
              id="notification-message"
              name="message"
              rows={6}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              placeholder="Enter notification text…"
              disabled={submitting}
            />
          </div>

          <fieldset className="rounded-md border border-gray-200 bg-white p-3 space-y-3">
            <legend className="text-sm font-medium text-gray-800 px-1">Display period</legend>
            <p className="text-xs text-gray-500 -mt-1">
              The notification is shown only between these date and times (sent to the server as ISO
              UTC).
            </p>
            <div className="flex flex-wrap gap-2 items-end">
              <div className="min-w-0 flex-1">
                <label htmlFor="notification-display-from" className="block text-xs font-medium text-gray-600 mb-1">
                  From
                </label>
                <input
                  id="notification-display-from"
                  type="datetime-local"
                  value={displayFrom}
                  onChange={(e) => setDisplayFrom(e.target.value)}
                  disabled={submitting}
                  className="w-full min-w-0 rounded-md border border-gray-300 px-2 py-1.5 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>
              <div className="min-w-0 flex-1">
                <label htmlFor="notification-display-to" className="block text-xs font-medium text-gray-600 mb-1">
                  To
                </label>
                <input
                  id="notification-display-to"
                  type="datetime-local"
                  value={displayTo}
                  onChange={(e) => setDisplayTo(e.target.value)}
                  disabled={submitting}
                  className="w-full min-w-0 rounded-md border border-gray-300 px-2 py-1.5 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>
            </div>
          </fieldset>

          <div className="rounded-md border border-gray-200 bg-gray-50 p-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={allTenants}
                onChange={(e) => {
                  setAllTenants(e.target.checked);
                  if (e.target.checked) {
                    setSelectedTenantIds([]);
                    setTenantSearch('');
                  }
                }}
                disabled={submitting}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm font-medium text-gray-800">All tenants</span>
            </label>
          </div>

          {!allTenants && (
            <div>
              <span className="block text-sm font-medium text-gray-700 mb-2">Select tenants</span>
              {loadingTenants ? (
                <p className="text-sm text-gray-500">Loading tenants…</p>
              ) : tenantOptions.length === 0 ? (
                <p className="text-sm text-amber-700">No tenants found.</p>
              ) : (
                <>
                  <div className="relative mb-2">
                    <MagnifyingGlassIcon
                      className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
                      aria-hidden
                    />
                    <input
                      id="tenant-search"
                      type="search"
                      value={tenantSearch}
                      onChange={(e) => setTenantSearch(e.target.value)}
                      placeholder="Search by name or ID…"
                      disabled={submitting}
                      autoComplete="off"
                      className="w-full rounded-md border border-gray-300 py-1.5 pl-9 pr-16 text-sm shadow-sm placeholder:text-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                      aria-label="Search tenants"
                    />
                    {tenantSearch.trim() ? (
                      <button
                        type="button"
                        onClick={() => setTenantSearch('')}
                        disabled={submitting}
                        className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded px-1.5 py-0.5 text-xs text-gray-500 hover:bg-gray-100 hover:text-gray-800"
                        aria-label="Clear search"
                      >
                        Clear
                      </button>
                    ) : null}
                  </div>
                  <p className="text-xs text-gray-500 mb-1.5">
                    {filteredTenantOptions.length === tenantOptions.length
                      ? `${tenantOptions.length} tenant${tenantOptions.length === 1 ? '' : 's'}`
                      : `${filteredTenantOptions.length} of ${tenantOptions.length} tenants`}
                  </p>
                  <div className="max-h-52 overflow-y-auto rounded-md border border-gray-200 divide-y divide-gray-100">
                    {filteredTenantOptions.length === 0 ? (
                      <p className="px-3 py-4 text-sm text-gray-500 text-center">
                        No tenants match your search.
                      </p>
                    ) : (
                      filteredTenantOptions.map((t) => (
                        <label
                          key={t.id}
                          className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer text-sm"
                        >
                          <input
                            type="checkbox"
                            checked={selectedTenantIds.includes(t.id)}
                            onChange={() => toggleTenant(t.id)}
                            disabled={submitting}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <span className="text-gray-800">
                            <span className="font-medium">{t.label}</span>
                            <span className="text-gray-400 ml-1.5 text-xs">#{t.id}</span>
                          </span>
                        </label>
                      ))
                    )}
                  </div>
                </>
              )}
            </div>
          )}

          <div className="flex flex-wrap gap-2 pt-2 border-t border-gray-100">
            <Button type="submit" disabled={submitting || clearing} isProcessing={submitting}>
              Save notification
            </Button>
            <button
              type="button"
              onClick={handleClose}
              disabled={submitting || clearing}
              className="px-3 py-1 text-sm rounded border border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
          </div>
        </form>
        ) : null}

        <div className={`flex flex-col gap-3 ${isSystemAdmin ? 'mt-6 pt-4 border-t border-gray-200' : ''}`}>
          <div>
            <h2 className="text-sm font-semibold text-gray-800">Clear all notifications</h2>
            <p className="text-xs text-gray-500 mt-1">
              {isSystemAdmin && !allTenants && selectedTenantIds.length === 1
                ? `Clears notifications for the selected tenant only.`
                : isSystemAdmin
                  ? 'Clears all notifications across every tenant.'
                  : 'Clears all notifications for your tenant.'}
            </p>
          </div>
          <Button
            type="button"
            disabled={submitting || clearing}
            isProcessing={clearing}
            onClick={() => setShowClearConfirm(true)}
            className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md text-sm w-fit"
          >
            Clear all
          </Button>
        </div>
      </SideDrawer>

      <ConfirmationDialog
        isOpen={showClearConfirm}
        title="Clear all notifications"
        message={clearConfirmMessage}
        onConfirm={() => void handleClearAll()}
        onCancel={() => {
          if (!clearing) setShowClearConfirm(false);
        }}
      />
    </div>
  );
};

export default CreateNotification;
